import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    List,
    CircularProgress,
    Alert,
    Box,
    Button
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks';
import { updateContractCenters } from '../../../../../../services/ContractService/contractSlice';
import { useGetCentersQuery } from '../../../../../../services/ConfigurationService';
import { useSearchParams } from 'react-router-dom';
import { CenterType } from '../../../../../../services/ConfigurationService/types/centerType';
import { useAddCenterDialog } from './hooks';
import { SelectAllHeader, CenterListItem, DialogActionButtons } from './components';

const AddCenterDialog: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const company = searchParams.get('company') || '';
    const contract = useAppSelector(state => state.contract.currentContract);
    const { data: allCenters, isLoading, error } = useGetCentersQuery(
        { company },
        { skip: !company }
    );

    const {
        selectedCodes,
        toggleCenter,
        toggleAll,
        resetSelection
    } = useAddCenterDialog();

    // Filter out centers already on contract
    const availableCenters = useMemo(() => {
        if (!allCenters || !contract?.centers) return allCenters || [];
        const existingCodes = new Set(contract.centers.map((c: { code: string }) => c.code));
        return allCenters.filter((center: CenterType) => !existingCodes.has(center.code));
    }, [allCenters, contract?.centers]);

    const allCodes = useMemo(() => 
        availableCenters.map((c: CenterType) => c.code), 
        [availableCenters]
    );

    const handleOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        resetSelection();
        setOpen(false);
    };

    const handleAdd = () => {
        if (!contract) return;

        const centersToAdd = availableCenters.filter(
            (center: CenterType) => selectedCodes.includes(center.code)
        ).map((center: CenterType) => ({
            code: center.code,
            name: center.name
        }));

        const updatedCenters = [
            ...(contract.centers || []),
            ...centersToAdd
        ];

        dispatch(updateContractCenters(updatedCenters));
        handleClose();
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpen}
                size="small"
            >
                Add Centers
            </Button>

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Add Centers to Contract</DialogTitle>

                <DialogContent>
                    {isLoading && (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Failed to load centers. Please try again.
                        </Alert>
                    )}

                    {!isLoading && !error && availableCenters.length === 0 && (
                        <Alert severity="info">
                            All centers have already been added to this contract.
                        </Alert>
                    )}

                    {!isLoading && !error && availableCenters.length > 0 && (
                        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <SelectAllHeader
                                totalCount={availableCenters.length}
                                selectedCount={selectedCodes.length}
                                onToggle={() => toggleAll(allCodes)}
                            />
                            {availableCenters.map((center: CenterType, index: number) => (
                                <CenterListItem
                                    key={center.code}
                                    center={center}
                                    isSelected={selectedCodes.includes(center.code)}
                                    showDivider={index > 0}
                                    onToggle={() => toggleCenter(center.code)}
                                />
                            ))}
                        </List>
                    )}
                </DialogContent>

                <DialogActionButtons
                    selectedCount={selectedCodes.length}
                    onCancel={handleClose}
                    onAdd={handleAdd}
                />
            </Dialog>
        </>
    );
};

export default AddCenterDialog;
